/**
 * ============================================================================
 * ZIvapp shared configuration
 * ============================================================================
 *
 * The shared package is consumed by two bundlers that disagree about how a
 * build reads its environment:
 *
 *   zivapp-web     Vite      import.meta.env.VITE_*
 *   zivapp-mobile  Metro     process.env.EXPO_PUBLIC_*
 *
 * Neither form survives the other bundler, so nothing under shared/ reads the
 * environment itself. Each app reads its own and hands the result to
 * configureZivapp() once, at boot, before rendering.
 *
 *     // zivapp-web/src/boot.js
 *     configureZivapp({ backend: import.meta.env.VITE_BACKEND, firebase: {...} })
 *
 * Everything downstream — the provider switch, Firebase initialisation, the
 * REST URLs — reads from here instead.
 */

/** @type {Config|null} */
let current = null

/**
 * @typedef {object} Config
 * @property {'mock'|'firebase'|'rest'} backend  which provider the app talks to
 * @property {object}  firebase   Firebase web config; ignored unless backend is 'firebase'
 * @property {string}  apiUrl     REST base URL; ignored unless backend is 'rest'
 * @property {string}  wsUrl      WebSocket URL; ignored unless backend is 'rest'
 * @property {boolean} dev        enables the startup contract check
 * @property {string}  platform   'web' | 'ios' | 'android', for provider branching
 * @property {TokenStorage|null} tokenStorage  where the REST auth token persists
 * @property {(() => unknown)|null} phoneVerifier  builds Firebase's SMS verifier
 */

/**
 * Key/value persistence for the auth token. Web passes a localStorage wrapper,
 * mobile passes AsyncStorage. Every method may return a promise.
 *
 * @typedef {object} TokenStorage
 * @property {(key: string) => string|null|Promise<string|null>} get
 * @property {(key: string, value: string) => void|Promise<void>} set
 * @property {(key: string) => void|Promise<void>} remove
 */

const DEFAULTS = {
  // The real server, not the mock. The mock remains a full implementation
  // of the contracts and is still what the shared-layer tests run against,
  // but nothing a person looks at should be fed invented data.
  backend:  'rest',
  firebase: {},
  apiUrl:   'http://localhost:4000',
  wsUrl:    'ws://localhost:4000',
  dev:      false,
  platform: 'web',
  tokenStorage: null,

  /**
   * Builds the verifier Firebase requires before it will send an SMS.
   *
   * Platform-specific, and injected for the same reason the environment is:
   * on the web it is an invisible reCAPTCHA mounted in a DOM node, and this
   * package may not touch `document` because Metro bundles it too. Supplied
   * by zivapp-web/src/boot.js.
   *
   * Left null on React Native. The Firebase JS SDK has no way to run
   * reCAPTCHA there, so native phone sign-in needs @react-native-firebase/auth
   * and a development build — see docs/FIREBASE-SETUP.md. Until then the
   * provider throws a message saying exactly that, rather than failing deep
   * inside the SDK.
   */
  phoneVerifier: null
}

const BACKENDS = ['mock', 'firebase', 'rest']

/**
 * Called once per app, at boot, before the first render.
 *
 * @param {Partial<Config>} next
 */
export function configureZivapp (next = {}) {
  const merged = { ...DEFAULTS, ...next }
  merged.firebase = { ...DEFAULTS.firebase, ...(next.firebase ?? {}) }

  if (!BACKENDS.includes(merged.backend)) {
    throw new Error(
      `[ZIvapp] Unknown backend "${merged.backend}". Expected one of: ${BACKENDS.join(', ')}.`
    )
  }

  if (merged.backend === 'firebase' && !isFirebaseUsable(merged.firebase)) {
    // Not fatal: the app still boots and the error surfaces on first use with a
    // message that says what to do, rather than a blank screen at startup.
    console.error(
      '[ZIvapp] backend is "firebase" but no Firebase keys were supplied.\n' +
      '  Web:    copy zivapp-web/.env.example to .env.local and fill it in.\n' +
      '  Mobile: copy zivapp-mobile/.env.example to .env and fill it in.\n' +
      '  Or set the backend to "mock" to work without an account.\n' +
      '  Step by step: docs/SETUP.md'
    )
  }

  current = merged
  return current
}

/** @returns {Config} */
export function getConfig () {
  if (!current) {
    throw new Error(
      '[ZIvapp] configureZivapp() has not been called.\n' +
      '  Each app must call it once at boot, before rendering:\n' +
      '    web    → zivapp-web/src/boot.js\n' +
      '    mobile → zivapp-mobile/boot.js'
    )
  }
  return current
}

/** True when the config carries enough to initialise Firebase. */
export function isFirebaseUsable (cfg = current?.firebase) {
  return Boolean(cfg?.apiKey && cfg?.projectId)
}

/** Which backend this build is talking to. */
export function currentBackend () {
  return getConfig().backend
}

/** Test seam: forget the configuration so a test can apply a different one. */
export function resetZivappConfig () {
  current = null
}
